import React, { useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { getEmailHistoryByContact } from "../service/adminContact_service";
import excelIcon from "../../../assets/images/SBA/excel_icon.png";
import { exportToExcel } from "../../../shared/utils/exportToExcel";
import useDocumentTitle from "../../../shared/customHooks/useDocumentTitle";

const EmailHistoryTab = () => {

  useDocumentTitle("Email History");
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [pageSize] = useState(15);
  const pick = (obj, ...keys) => {
    for (const k of keys) {
      if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
    }
    return "";
  };

  const formatDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const mm = `${d.getMonth() + 1}`.padStart(2, "0");
    const dd = `${d.getDate()}`.padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}-${dd}-${yyyy}`; 
  };

  const loadGrid = async () => {
    try {
      setLoading(true);
      setErrorMsg("");
      const res = await getEmailHistoryByContact(4); 
      setRowData(res?.data || []);
    } catch {
      setRowData([]);
      setErrorMsg("Failed to load email history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGrid();
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Subject",
        minWidth: 280,
        flex: 1.4,
        sortable: true,
        filter: true,
        filter:'agTextColumnFilter',
        valueGetter: (p) => pick(p.data, "subject", "Subject"),
      },
      {
        headerName: "Date",
        minWidth: 140,
        sortable: true,
        filter: true,
        filter:'agTextColumnFilter',
        valueGetter: (p) => formatDate(pick(p.data, "sendDate", "SendDate")),
      },
      {
        headerName: "Created By",
        minWidth: 180,
        flex: 1,
        sortable: true,
        filter:'agTextColumnFilter',
        valueGetter: (p) => pick(p.data, "createdName", "CreatedName"),
      },
      {
        headerName: "Sent",
        minWidth: 110,
        sortable: true,
        filter: "agNumberColumnFilter",
        valueGetter: (p) => Number(pick(p.data, "recipientsCount", "RecipientsCount") || 0),
      },
      {
        headerName: "Alerts",
        minWidth: 110,
        sortable: true,
        filter: "agNumberColumnFilter",
        valueGetter: (p) => Number(pick(p.data, "documentCount", "DocumentCount") || 0),
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      floatingFilter: true,
    }),
    []
  );

  const handleExport = () => {
    const headers = ["Subject", "Date", "Created By", "Sent", "Alerts"];
    const rows = (rowData || []).map((r) => ({
      Subject: pick(r, "subject", "Subject"),
      Date: formatDate(pick(r, "sendDate", "SendDate")),
      "Created By": pick(r, "createdName", "CreatedName"),
      Sent: Number(pick(r, "recipientsCount", "RecipientsCount") || 0),
      Alerts: Number(pick(r, "documentCount", "DocumentCount") || 0),
    }));

    exportToExcel({
      fileName: "Email History.xlsx", 
      headers,
      rows,
    });
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button
          type="button"
          title="Export to Excel"
          onClick={handleExport}
          style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer" }}
        >
          <img
            src={excelIcon} 
            alt="Export"
            style={{ width: 28, height: 28 }}
          />
        </button>
      </div>

      {errorMsg ? <div style={{ color: "red", marginBottom: 8 }}>{errorMsg}</div> : null}

      <div className="ag-theme-alpine" style={{ width: "100%", height: 520 }}>
        <AgGridReact
          theme={"legacy"}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationPageSize={pageSize}
          paginationPageSizeSelector={[10, 15, 50, 100, 200]}
          suppressCellFocus
          overlayNoRowsTemplate={loading ? "Loading..." : "No Records Available"}
        />
      </div>
    </div>
  );
};

export default EmailHistoryTab;
